import { useSyncExternalStore } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { z, ZodType } from 'zod'

type ParsedStorageStrategy = 'local' | 'cookie'

interface UseParsedStorageBaseParams<Schema extends ZodType> {
  initialValue: z.infer<Schema>
  schema: Schema
}

export type UseParsedStorageParams<Schema extends ZodType> =
  UseParsedStorageBaseParams<Schema> &
    (
      | {
          cookie?: never
          strategy: 'local'
        }
      | {
          cookie?: ParsedStorageCookieOptions
          strategy: 'cookie'
        }
    )

interface ParsedStorageCookieOptions {
  domain?: string
  maxAgeSeconds?: number
  path?: string
  sameSite?: 'Strict' | 'Lax' | 'None'
}

const ParsedStorageChangedEvent = 'joyful-parsed-storage-change'
const DefaultCookieMaxAgeSeconds = 60 * 60 * 24 * 365
const ParsedStorageSnapshotCache = new Map<
  string,
  {
    initialValue: unknown
    rawValue: string | null
    schema: ZodType
    value: unknown
  }
>()

export function useParsedStorage<Schema extends ZodType>(
  key: string,
  params: UseParsedStorageParams<Schema>,
): [z.infer<Schema>, Dispatch<SetStateAction<z.infer<Schema>>>] {
  const value = useSyncExternalStore(
    subscribeToParsedStorage,
    () => {
      return getParsedStorageValue(key, params)
    },
    () => {
      return params.initialValue
    },
  )

  const setParsedValue: Dispatch<SetStateAction<z.infer<Schema>>> = (
    nextValue,
  ) => {
    setParsedStorageValue(key, nextValue, params)
  }

  return [value, setParsedValue]
}

/**
 * Write outside React, for effects that would otherwise depend on the unstable
 * setter identity `useParsedStorage` returns. Subscribed components still update.
 */
export function setParsedStorageValue<Schema extends ZodType>(
  key: string,
  nextValue: SetStateAction<z.infer<Schema>>,
  params: UseParsedStorageParams<Schema>,
) {
  const currentValue = getParsedStorageValue(key, params)
  const valueToStore = isSetStateUpdater(nextValue)
    ? nextValue(currentValue)
    : nextValue

  setStorageValue(key, valueToStore, params)
  globalThis.dispatchEvent(new Event(ParsedStorageChangedEvent))
}

export function removeParsedStorageValue(key: string) {
  try {
    localStorage.removeItem(key)
  } catch {
    return
  }

  globalThis.dispatchEvent(new Event(ParsedStorageChangedEvent))
}

function subscribeToParsedStorage(onChange: () => void) {
  globalThis.addEventListener(ParsedStorageChangedEvent, onChange)
  globalThis.addEventListener('storage', onChange)
  globalThis.addEventListener('focus', onChange)
  document.addEventListener('visibilitychange', onChange)

  return () => {
    globalThis.removeEventListener(ParsedStorageChangedEvent, onChange)
    globalThis.removeEventListener('storage', onChange)
    globalThis.removeEventListener('focus', onChange)
    document.removeEventListener('visibilitychange', onChange)
  }
}

function getParsedStorageValue<Schema extends ZodType>(
  key: string,
  { initialValue, schema, strategy }: UseParsedStorageParams<Schema>,
) {
  const rawValue = getRawStorageValue(key, strategy)
  const cacheKey = `${strategy}:${key}`
  const cachedSnapshot = ParsedStorageSnapshotCache.get(cacheKey)
  if (
    cachedSnapshot?.rawValue === rawValue &&
    cachedSnapshot.initialValue === initialValue &&
    cachedSnapshot.schema === schema
  ) {
    // SAFETY: this cache hit matched schema, initialValue, and rawValue for Schema.
    return cachedSnapshot.value as z.infer<Schema>
  }

  const value = parseStorageValue(rawValue, {
    initialValue,
    schema,
  })
  ParsedStorageSnapshotCache.set(cacheKey, {
    initialValue,
    rawValue,
    schema,
    value,
  })
  return value
}

function parseStorageValue<Schema extends ZodType>(
  rawValue: string | null,
  { initialValue, schema }: UseParsedStorageBaseParams<Schema>,
) {
  if (rawValue === null) {
    return initialValue
  }

  try {
    const storedValue = JSON.parse(rawValue)
    const parsedValue = schema.safeParse(storedValue)
    return parsedValue.success ? parsedValue.data : initialValue
  } catch {
    return initialValue
  }
}

function getRawStorageValue(key: string, strategy: ParsedStorageStrategy) {
  return strategy === 'local' ? getLocalStorageValue(key) : getCookieValue(key)
}

function getLocalStorageValue(key: string) {
  if (typeof localStorage === 'undefined') {
    return null
  }

  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function getCookieValue(key: string) {
  if (typeof document === 'undefined') {
    return null
  }

  const cookiePrefix = `${key}=`
  const cookie = document.cookie.split('; ').find((cookiePart) => {
    return cookiePart.startsWith(cookiePrefix)
  })
  if (!cookie) {
    return null
  }

  const encodedValue = cookie.slice(cookiePrefix.length)
  try {
    return decodeURIComponent(encodedValue)
  } catch {
    return null
  }
}

function setStorageValue<Schema extends ZodType>(
  key: string,
  value: z.infer<Schema>,
  { cookie, strategy }: UseParsedStorageParams<Schema>,
) {
  const encodedValue = JSON.stringify(value)
  if (encodedValue === undefined) {
    return
  }

  if (strategy === 'local') {
    setLocalStorageValue(key, encodedValue)
    return
  }

  setCookieValue(key, encodedValue, cookie)
}

function setLocalStorageValue(key: string, encodedValue: string) {
  if (typeof localStorage === 'undefined') {
    return
  }

  try {
    localStorage.setItem(key, encodedValue)
  } catch {
    return
  }
}

function setCookieValue(
  key: string,
  encodedValue: string,
  {
    domain,
    maxAgeSeconds = DefaultCookieMaxAgeSeconds,
    path = '/',
    sameSite = 'Lax',
  }: ParsedStorageCookieOptions = {},
) {
  const cookieParts = [
    `${key}=${encodeURIComponent(encodedValue)}`,
    `Path=${path}`,
    `Max-Age=${maxAgeSeconds}`,
    `SameSite=${sameSite}`,
  ]

  if (domain) {
    cookieParts.push(`Domain=${domain}`)
  }

  if (sameSite === 'None' || globalThis.location.protocol === 'https:') {
    cookieParts.push('Secure')
  }

  document.cookie = cookieParts.join('; ')
}

function isSetStateUpdater<Value>(
  nextValue: SetStateAction<Value>,
): nextValue is (currentValue: Value) => Value {
  return typeof nextValue === 'function'
}
